import { useCallback, useEffect, useState } from "react";
import { Ticket, Plus, Copy, Ban, BadgeCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Admin coupon manager: issue free-membership or $-off codes with expiry,
   issued-to and note tracking + redemption audit trail. Moderator-only. */

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: "free_year", plan: "individual", amount_off: "25",
    code: "", issued_to: "", note: "", expires_at: "", max_uses: 1 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/coupons").then((r) => setItems(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const create = async () => {
    setBusy(true);
    try {
      const body = { ...form, max_uses: parseInt(form.max_uses) || 1,
        amount_off: form.type === "amount_off" ? parseFloat(form.amount_off) : null,
        code: form.code || null, expires_at: form.expires_at || null,
        issued_to: form.issued_to || null, note: form.note || null };
      const r = await api.post("/admin/coupons", body);
      toast.success(`Coupon ${r.data.code} created`);
      setForm((f) => ({ ...f, code: "", issued_to: "", note: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create coupon"); }
    finally { setBusy(false); }
  };

  const deactivate = async (id) => {
    try { await api.delete(`/admin/coupons/${id}`); load(); } catch { toast.error("Failed"); }
  };

  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success(`${code} copied`); };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-coupons-page">
      <h1 className="font-heading font-900 text-3xl sm:text-4xl flex items-center gap-3 mb-2">
        <Ticket className="w-8 h-8 text-violet-600" /> Membership Coupons
      </h1>
      <p className="text-muted-foreground mb-8">Issue free-membership or discount codes to specific users. Codes auto-expire and every redemption is tracked.</p>

      {/* Create form */}
      <div className="border border-violet-300 bg-violet-50/40 p-5 mb-8" data-testid="coupon-create-form">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="text-sm"><span className="font-700 block mb-1">Type</span>
            <select value={form.type} onChange={set("type")} data-testid="coupon-type-select" className="w-full border border-zinc-300 px-2 py-2 bg-white">
              <option value="free_year">Free membership (1 year)</option>
              <option value="amount_off">$ amount off</option>
            </select></label>
          {form.type === "free_year" ? (
            <label className="text-sm"><span className="font-700 block mb-1">Plan granted</span>
              <select value={form.plan} onChange={set("plan")} data-testid="coupon-plan-select" className="w-full border border-zinc-300 px-2 py-2 bg-white">
                <option value="individual">Individual</option><option value="couple">Couple</option>
                <option value="family">Family</option><option value="family_plus">Family Plus</option>
              </select></label>
          ) : (
            <label className="text-sm"><span className="font-700 block mb-1">Discount ($)</span>
              <input value={form.amount_off} onChange={set("amount_off")} type="number" min="1" max="500" data-testid="coupon-amount-input"
                className="w-full border border-zinc-300 px-2 py-2" /></label>
          )}
          <label className="text-sm"><span className="font-700 block mb-1">Expires on <span className="font-400 text-muted-foreground">(auto-expiry)</span></span>
            <input value={form.expires_at} onChange={set("expires_at")} type="date" data-testid="coupon-expiry-input"
              className="w-full border border-zinc-300 px-2 py-2 bg-white" /></label>
          <label className="text-sm"><span className="font-700 block mb-1">Max uses</span>
            <input value={form.max_uses} onChange={set("max_uses")} type="number" min="1" max="10000" data-testid="coupon-maxuses-input"
              className="w-full border border-zinc-300 px-2 py-2" /></label>
          <label className="text-sm"><span className="font-700 block mb-1">Custom code <span className="font-400 text-muted-foreground">(blank = auto)</span></span>
            <input value={form.code} onChange={set("code")} placeholder="TP-WELCOME25" data-testid="coupon-code-input"
              className="w-full border border-zinc-300 px-2 py-2" /></label>
          <label className="text-sm"><span className="font-700 block mb-1">Issued to</span>
            <input value={form.issued_to} onChange={set("issued_to")} placeholder="jane@email.com / Jane D." data-testid="coupon-issuedto-input"
              className="w-full border border-zinc-300 px-2 py-2" /></label>
          <label className="text-sm sm:col-span-2"><span className="font-700 block mb-1">Note / comment</span>
            <input value={form.note} onChange={set("note")} placeholder="Why this coupon was issued…" data-testid="coupon-note-input"
              className="w-full border border-zinc-300 px-2 py-2" /></label>
        </div>
        <button onClick={create} disabled={busy} data-testid="coupon-create-btn"
          className="mt-4 inline-flex items-center gap-1.5 bg-violet-700 text-white font-700 px-5 py-2.5 text-sm hover:bg-violet-600 disabled:opacity-50">
          <Plus className="w-4 h-4" /> {busy ? "Creating…" : "Create coupon"}
        </button>
      </div>

      {/* List */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-zinc-300">
          <thead className="bg-zinc-100 text-left">
            <tr>
              {["Code", "Value", "Issued to", "Note", "Created", "Expires", "Uses", "Status", ""].map((h) => (
                <th key={h} className="px-3 py-2 font-700 border-b border-zinc-300">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-zinc-200 align-top" data-testid={`coupon-row-${c.code}`}>
                <td className="px-3 py-2 font-mono font-700 whitespace-nowrap">
                  {c.code}
                  <button onClick={() => copy(c.code)} className="ml-1.5 text-muted-foreground hover:text-violet-700" title="Copy"><Copy className="w-3.5 h-3.5 inline" /></button>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {c.type === "free_year"
                    ? <span className="text-emerald-700 font-600 capitalize">Free {(c.plan || "individual").replace("_", " ")} · 1yr</span>
                    : <span className="text-violet-700 font-600">${(c.amount_off_cents / 100).toFixed(2)} off</span>}
                </td>
                <td className="px-3 py-2 max-w-[160px] truncate">{c.issued_to || "—"}</td>
                <td className="px-3 py-2 max-w-[220px]">{c.note || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmt(c.created_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{c.expires_at ? fmt(c.expires_at) : "Never"}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {c.uses}/{c.max_uses}
                  {c.redemptions.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {c.redemptions.map((r) => <div key={r.id}>{r.email || r.user_id} · {fmt(r.redeemed_at)}</div>)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {!c.active ? <span className="inline-flex items-center gap-1 text-zinc-500"><Ban className="w-3.5 h-3.5" /> Deactivated</span>
                    : c.expired ? <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="w-3.5 h-3.5" /> Expired</span>
                    : c.uses >= c.max_uses ? <span className="text-zinc-500">Used up</span>
                    : <span className="inline-flex items-center gap-1 text-emerald-700"><BadgeCheck className="w-3.5 h-3.5" /> Active</span>}
                </td>
                <td className="px-3 py-2">
                  {c.active && <button onClick={() => deactivate(c.id)} data-testid={`coupon-deactivate-${c.code}`}
                    className="text-xs text-rose-600 hover:underline">Deactivate</button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No coupons yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
